import { z } from "zod";

/**
 * `/api/strategies/:id/backtests/*` request + response contracts.
 *
 * A backtest is an async job: `POST` enqueues a `queued` row and returns `202
 * { id }`; the queue worker fetches history, runs the pure engine, and persists
 * the `BacktestResult`. The list/detail reads mirror the `backtests` row.
 *
 * Money/quantity/price fields inside a result are **decimal strings** (the engine
 * never emits floats for cash); the summary ratios are plain numbers. These
 * schemas mirror `@richrich/core/domain/backtest` `BacktestResult` — the engine's
 * type is the binding source of truth; this is its wire projection.
 */

// ── shared scalars ──────────────────────────────────────────────
/** Calendar day `YYYY-MM-DD`. */
export const BacktestDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be a YYYY-MM-DD calendar date");

/** The two markets richrich backtests. */
export const BacktestCurrencySchema = z.enum(["KRW", "USD"]);
export type BacktestCurrency = z.infer<typeof BacktestCurrencySchema>;

export const BacktestStatusSchema = z.enum(["queued", "running", "done", "error"]);
export type BacktestStatus = z.infer<typeof BacktestStatusSchema>;

/** A positive canonical decimal string (never a float, never zero/negative). */
const PositiveDecimalString = z
  .string()
  .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/, "must be a canonical decimal string")
  .refine((s) => Number(s) > 0, "must be positive");

/** Basis points, 0..1000 (1 bp = 0.01%). */
const Bps = z.number().int().min(0).max(1000);

// ── request ─────────────────────────────────────────────────────
/**
 * The stored/immutable backtest parameters (`params_json`). `POST` validates a
 * body of exactly this shape (plus `from < to`); the worker re-parses it here.
 */
export const BacktestParamsSchema = z.object({
  from: BacktestDateSchema,
  to: BacktestDateSchema,
  /**
   * Equity base the sim seeds. ABSENT ⇒ the server defaults it to the
   * strategy's resolved budget (row override > dsl.risk.budget) — the live
   * analogue, so rebalance/targetWeight rehearse the sizes the strategy will
   * actually trade. An explicit value that differs from the budget still runs,
   * flagged with an `INITIAL_CASH_DIFFERS_FROM_BUDGET` coverage warning.
   */
  initialCash: PositiveDecimalString.optional(),
  currency: BacktestCurrencySchema,
  feeBps: Bps.optional(),
  slippageBps: Bps.optional(),
  /**
   * Opt-in: replay the strategy's `ai-advisor` gate on every historical trigger
   * (a full model call per bar that produced orders). Only meaningful when the
   * strategy's execution mode is `ai-advisor`; ignored otherwise. Off by default —
   * a plain replay runs the deterministic rules alone.
   */
  useAdvisor: z.boolean().optional(),
});
export type BacktestParams = z.infer<typeof BacktestParamsSchema>;

/** `POST /api/strategies/:id/backtests` body. */
export const CreateBacktestSchema = BacktestParamsSchema.refine((v) => v.from < v.to, {
  message: "from must be strictly before to",
  path: ["from"],
});
export type CreateBacktest = z.infer<typeof CreateBacktestSchema>;

/** `POST` response — the queued backtest id (HTTP 202). */
export const CreateBacktestResponseSchema = z.object({ id: z.string().min(1) });
export type CreateBacktestResponse = z.infer<typeof CreateBacktestResponseSchema>;

// ── result (mirror of BacktestResult) ───────────────────────────
export const BacktestCoverageSchema = z.object({
  requestedFrom: z.string(),
  requestedTo: z.string(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
  warnings: z.array(z.string()),
});

export const BacktestEquityPointSchema = z.object({
  date: z.string(),
  equity: z.string(),
  cash: z.string(),
});

export const BacktestTradeSchema = z.object({
  date: z.string(),
  symbol: z.string(),
  side: z.enum(["buy", "sell"]),
  qty: z.string(),
  price: z.string(),
  notional: z.string(),
  fee: z.string(),
  ruleId: z.string().nullable(),
});

export const BacktestMetricsSchema = z.object({
  totalReturnPct: z.number(),
  cagrPct: z.number().nullable(),
  maxDrawdownPct: z.number(),
  winRate: z.number().nullable(),
  payoffRatio: z.number().nullable(),
  tradeCount: z.number(),
  roundTrips: z.number(),
});

export const BacktestBenchmarkMetricsSchema = z
  .object({
    totalReturnPct: z.number(),
    cagrPct: z.number().nullable(),
    maxDrawdownPct: z.number(),
  })
  .nullable();

/** One advisor verdict on a bar's produced orders (mirrors BacktestAdvisorDecision). */
export const BacktestAdvisorDecisionSchema = z.object({
  date: z.string(),
  verdict: z.enum(["approve", "veto", "resize"]),
  sizeMultiplier: z.number().optional(),
  reasoning: z.string(),
});
export type BacktestAdvisorDecisionDto = z.infer<typeof BacktestAdvisorDecisionSchema>;

/** One symbol's adjusted daily close series over the simulated window (for the overlay chart). */
export const BacktestPriceSeriesSchema = z.object({
  symbol: z.string(),
  /** `primary` = declared universe symbol; `watch` = watch-list only. */
  kind: z.enum(["primary", "watch"]),
  points: z.array(z.object({ date: z.string(), close: z.number() })),
});
export type BacktestPriceSeriesDto = z.infer<typeof BacktestPriceSeriesSchema>;

export const BacktestResultSchema = z.object({
  coverage: BacktestCoverageSchema,
  equityCurve: z.array(BacktestEquityPointSchema),
  benchmarkCurve: z.array(BacktestEquityPointSchema).nullable(),
  trades: z.array(BacktestTradeSchema),
  metrics: BacktestMetricsSchema,
  benchmarkMetrics: BacktestBenchmarkMetricsSchema,
  /** Present only when an advisor gate ran (opt-in `useAdvisor`) and produced ≥1 decision. */
  advisorDecisions: z.array(BacktestAdvisorDecisionSchema).optional(),
  /** Per-symbol close series for the overlay chart; omitted when no data was priced. */
  priceSeries: z.array(BacktestPriceSeriesSchema).optional(),
  /** Daily Fear & Greed series over the window, when the run fetched FGI. */
  fgiSeries: z.array(z.object({ date: z.string(), score: z.number() })).optional(),
});
export type BacktestResultDto = z.infer<typeof BacktestResultSchema>;

// ── list + detail projections ───────────────────────────────────
/** Condensed headline metrics shown in the recent-runs list (only when `done`). */
export const BacktestHeadlineSchema = z.object({
  totalReturnPct: z.number(),
  cagrPct: z.number().nullable(),
  maxDrawdownPct: z.number(),
  winRate: z.number().nullable(),
  tradeCount: z.number(),
});
export type BacktestHeadline = z.infer<typeof BacktestHeadlineSchema>;

export const BacktestSummarySchema = z.object({
  id: z.string(),
  strategyId: z.string(),
  status: BacktestStatusSchema,
  from: z.string(),
  to: z.string(),
  initialCash: z.string(),
  currency: BacktestCurrencySchema,
  feeBps: z.number().nullable(),
  slippageBps: z.number().nullable(),
  createdAt: z.number().int(),
  startedAt: z.number().int().nullable(),
  finishedAt: z.number().int().nullable(),
  error: z.string().nullable(),
  /** Present only for a `done` run. */
  headline: BacktestHeadlineSchema.nullable(),
});
export type BacktestSummary = z.infer<typeof BacktestSummarySchema>;

/** `GET /api/strategies/:id/backtests`. */
export const ListBacktestsResponseSchema = z.object({
  backtests: z.array(BacktestSummarySchema),
});
export type ListBacktestsResponse = z.infer<typeof ListBacktestsResponseSchema>;

/** `GET /api/strategies/:id/backtests/:backtestId` — full row incl. the result. */
export const BacktestDetailSchema = BacktestSummarySchema.extend({
  result: BacktestResultSchema.nullable(),
});
export type BacktestDetail = z.infer<typeof BacktestDetailSchema>;
